import { createLocation } from './fork/history/LocationUtils';
import { createPath } from './fork/history/PathUtils';
import createTransitionManager from './fork/history/createTransitionManager';

/**
 * A custom history implementation for nested apps (granite remotes and the local app rendered by GraniteLocalApp).
 *
 * Offers the same surface area as createBrowserHistory but with one key
 * difference: it never registers its own window.popstate listener. The host
 * (usePopstateCapture) owns all Back/Forward event handling and calls
 * history.replace() to keep this history's location in sync.
 *
 * All browser side-effects (pushState, replaceState, go) are gated on a
 * BrowserHistoryAPI reference that is set via _connect() and cleared by the
 * returned cleanup. Calling cleanup() fully severs the tie to the browser,
 * so an unmounting remote app cannot manipulate browser history.
 */

export function createNestedHistory(basename, initialPath, initialSearch = '') {
  let browser = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const transitionManager = createTransitionManager();
  function createKey() {
    return Math.random().toString(36).substring(2, 8);
  }
  let currentLocation = createLocation(initialPath + initialSearch, undefined, createKey());
  let currentAction = 'POP';
  function setState(action, location) {
    currentAction = action;
    currentLocation = location;
    transitionManager.notifyListeners(location, action);
  }
  const history = {
    get length() {
      return browser ? browser.length : 1;
    },
    get action() {
      return currentAction;
    },
    get location() {
      return currentLocation;
    },
    push(path, state) {
      const location = createLocation(path, state, createKey(), currentLocation);
      transitionManager.confirmTransitionTo(location, 'PUSH', undefined, ok => {
        var _browser;
        if (!ok) return;
        (_browser = browser) === null || _browser === void 0 || _browser.pushState({
          key: location.key
        }, '', basename + createPath(location));
        setState('PUSH', location);
      });
    },
    replace(path, state) {
      const location = createLocation(path, state, createKey(), currentLocation);
      transitionManager.confirmTransitionTo(location, 'REPLACE', undefined, ok => {
        var _browser2, _browser$state, _browser3;
        if (!ok) return;
        (_browser2 = browser) === null || _browser2 === void 0 || _browser2.replaceState((_browser$state = (_browser3 = browser) === null || _browser3 === void 0 ? void 0 : _browser3.state) !== null && _browser$state !== void 0 ? _browser$state : null, '', basename + createPath(location));
        setState('REPLACE', location);
      });
    },
    go(n) {
      // Delegated to the browser when connected; no-op when severed or n===0.
      // go(0) would reload the page in some browsers — skip it.
      // The location sync happens asynchronously — the host catches the
      // resulting popstate and calls history.replace() to update location.
      if (n !== 0) {
        var _browser4;
        (_browser4 = browser) === null || _browser4 === void 0 || _browser4.go(n);
      }
    },
    goBack() {
      history.go(-1);
    },
    goForward() {
      history.go(1);
    },
    listen(listener) {
      return transitionManager.appendListener(listener);
    },
    block(prompt = false) {
      // Tracks the prompt for push/replace confirmation within the remote.
      // Does NOT register a DOM popstate listener — the host owns Back/Forward.
      return transitionManager.setPrompt(prompt);
    },
    createHref(location) {
      return basename + createPath(location);
    },
    _connect(b) {
      browser = b;
      return () => {
        browser = null;
      };
    }
  };
  return history;
}